import React from 'react';
import { ShoppingBag, Store, PackageCheck, Bike, Truck, MapPin } from 'lucide-react';
import { cn } from '../../lib/utils';

interface OrderTimelineProps {
  currentState: string;
  /** Courier's first name, when assigned — used to personalize sub-labels. */
  courierName?: string;
}

// Each step maps one or more backend states to a clear, shopper-friendly label.
// The sub-label (per exact state) explains what's happening *right now* so the
// shopper isn't left guessing what "Processing" means.
interface Step {
  key: string;
  states: string[];
  label: string;
  icon: React.ElementType;
}

const steps: Step[] = [
  {
    key: 'placed',
    states: ['CREATED', 'CART_VALIDATED', 'INVENTORY_RESERVED', 'PRICED', 'PAYMENT_AUTHORIZED'],
    label: 'Order Placed',
    icon: ShoppingBag,
  },
  {
    key: 'confirming',
    states: ['PENDING_BUSINESS_APPROVAL', 'BUSINESS_APPROVED'],
    label: 'Confirming with Boutique',
    icon: Store,
  },
  {
    key: 'finding',
    states: ['DISPATCHING_DRIVER', 'DRIVER_ASSIGNED'],
    label: 'Preparing & Finding Courier',
    icon: PackageCheck,
  },
  {
    key: 'pickup',
    states: ['PICKED_UP'],
    label: 'Picked Up',
    icon: Bike,
  },
  {
    key: 'delivering',
    states: ['OUT_FOR_DELIVERY'],
    label: 'On the Way to You',
    icon: Truck,
  },
  {
    key: 'delivered',
    states: ['DELIVERED', 'COMPLETED'],
    label: 'Delivered',
    icon: MapPin,
  },
];

/** Exact, human sub-label for the state the order is currently in. */
function subLabelFor(state: string, courierName?: string): string {
  const who = courierName ? courierName.split(' ')[0] : 'A courier';
  switch (state) {
    case 'CREATED':
    case 'CART_VALIDATED':
    case 'INVENTORY_RESERVED':
    case 'PRICED':
      return 'Setting up your order…';
    case 'PAYMENT_AUTHORIZED':
      return 'Payment confirmed';
    case 'PENDING_BUSINESS_APPROVAL':
      return 'Waiting for the boutique to accept';
    case 'BUSINESS_APPROVED':
      return 'Boutique accepted — packing your order';
    case 'DISPATCHING_DRIVER':
      return 'Finding a courier nearby';
    case 'DRIVER_ASSIGNED':
      return courierName ? `${who} is driving to the boutique` : 'Courier is driving to the boutique';
    case 'PICKED_UP':
      return courierName ? `${who} picked up your order` : 'Courier picked up your order';
    case 'OUT_FOR_DELIVERY':
      return courierName ? `${who} is on the way to you` : 'Heading to your address';
    case 'DELIVERED':
      return 'Delivered — enjoy!';
    case 'COMPLETED':
      return 'Order complete';
    default:
      return state.replace(/_/g, ' ');
  }
}

export function OrderTimeline({ currentState, courierName }: OrderTimelineProps) {
  const currentStepIndex = steps.findIndex((step) => step.states.includes(currentState));
  const activeIndex = currentStepIndex === -1 ? 0 : currentStepIndex;

  return (
    <div className="relative pl-8 space-y-8 before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-[2px] before:bg-purple-100">
      {steps.map((step, index) => {
        const isCompleted = index < activeIndex;
        const isActive = index === activeIndex;
        const Icon = step.icon;

        return (
          <div key={step.key} className="relative">
            {/* Timeline Dot/Icon */}
            <div
              className={cn(
                'absolute -left-[39px] flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors duration-300',
                isCompleted || isActive
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-200 bg-white text-gray-300'
              )}
            >
              <Icon className="h-4 w-4" />
            </div>

            {/* Content */}
            <div className={cn('transition-opacity duration-300', isActive ? 'opacity-100' : 'opacity-70')}>
              <p
                className={cn(
                  'font-serif font-medium',
                  isCompleted || isActive ? 'text-heading' : 'text-gray-400'
                )}
              >
                {step.label}
              </p>
              {isActive && (
                <p className="text-sm text-primary mt-1 font-medium">
                  {subLabelFor(currentState, courierName)}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

